import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import toast from 'react-hot-toast';

const RefinementStep = ({ 
  currentContent, 
  onRefine,
  isRefining 
}) => {
  const [refinementInput, setRefinementInput] = useState('');

  const quickRefinements = [
    {
      label: 'Ajouter la partie irrigation',
      prompt: 'Ajoute une section détaillée sur l\'irrigation endodontique utilisée',
      icon: 'Droplet'
    },
    {
      label: 'Rendre plus court',
      prompt: 'Raccourcis ce compte rendu en gardant toutes les informations essentielles',
      icon: 'Minimize2'
    },
    {
      label: 'Ajouter la conclusion pour le confrère',
      prompt: 'Ajoute une conclusion détaillée destinée au confrère adresseur avec les recommandations de suivi',
      icon: 'FileText'
    },
    {
      label: 'Développer la partie clinique',
      prompt: 'Développe davantage la partie examen clinique avec plus de détails',
      icon: 'Maximize2'
    },
    {
      label: 'Ajouter les prescriptions',
      prompt: 'Ajoute une section prescriptions avec les médicaments et posologie recommandés',
      icon: 'Pill'
    },
    {
      label: 'Améliorer le style',
      prompt: 'Améliore la formulation et le style professionnel sans changer le contenu',
      icon: 'Sparkles'
    }
  ];

  const handleQuickRefinement = (prompt) => {
    setRefinementInput(prompt);
  };

  const handleRefine = async () => {
    if (!refinementInput?.trim()) {
      toast?.error('Veuillez saisir une instruction de modification');
      return;
    }

    await onRefine(refinementInput);
    setRefinementInput('');
  };

  return (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">Ajustements IA</h2>
        <p className="text-gray-400">Demandez des modifications spécifiques au compte rendu</p>
      </div>

      {/* Quick Refinement Buttons */}
      <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Icon name="Zap" size={20} className="text-yellow-400" />
          Ajustements rapides
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {quickRefinements?.map((refinement, index) => (
            <button
              key={index}
              type="button"
              onClick={() => handleQuickRefinement(refinement?.prompt)}
              className="flex items-center gap-3 p-4 bg-gray-800/50 hover:bg-gray-700/50 border border-white/10 hover:border-blue-500/30 rounded-xl transition-all duration-300 text-left"
            >
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <Icon name={refinement?.icon} size={18} className="text-blue-400" />
              </div>
              <span className="text-gray-300 text-sm">{refinement?.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Custom Refinement Input */}
      <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Icon name="MessageSquare" size={20} className="text-purple-400" />
          Instruction personnalisée
        </h3>
        <div className="space-y-4">
          <textarea
            value={refinementInput}
            onChange={(e) => setRefinementInput(e?.target?.value)}
            placeholder="Exemple : rajoute la partie irrigation, rends plus court, ajoute la conclusion pour le confrère..."
            rows={4}
            className="w-full px-4 py-3 bg-gray-900/50 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50 resize-none"
          />
          <Button
            variant="default"
            onClick={handleRefine}
            loading={isRefining}
            disabled={!refinementInput?.trim()}
            iconName="Wand2"
            iconPosition="left"
            className="w-full"
          >
            Appliquer la modification
          </Button>
        </div>
      </div>

      {/* Current Content Preview */}
      <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Icon name="Eye" size={20} className="text-green-400" />
          Aperçu actuel
        </h3>
        <div className="max-h-96 overflow-y-auto">
          <div className="prose prose-invert max-w-none">
            <div className="whitespace-pre-wrap text-gray-300 text-sm leading-relaxed">
              {currentContent}
            </div>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-6">
        <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
          <Icon name="Info" size={18} className="text-purple-400" />
          Comment ça marche ?
        </h4>
        <ul className="space-y-2 text-sm text-gray-300">
          <li className="flex items-start gap-2">
            <Icon name="Check" size={16} className="text-green-400 mt-0.5" />
            <span>L'IA complète votre compte rendu sans réinventer le contenu existant</span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="Check" size={16} className="text-green-400 mt-0.5" />
            <span>Vous pouvez appliquer plusieurs modifications successives</span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="Check" size={16} className="text-green-400 mt-0.5" />
            <span>Chaque modification est appliquée au contenu actuel</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default RefinementStep;